import nthCheck from 'nth-check';
import consolv from 'lib/consolv';
import getDistanceFromElements from 'lib/getDistanceFromElements';

const defaultConfig = {
  findSlotsOnLoad: [],
  slotDefinitions: {},
};

let previousRequest;

/**
 * Finds ad slots on the page load and initializes them
 * @param {MCPInstance} MCP - Reference to the MCP instance
 * @param {Object} params
 * @param {Element} [params.parentElement] Parent element to search for new ad slots
 * @param {Function} params.addAsyncEvent Function provided by event dispatcher to add asynchronous
 * functions before continuing current processing thread
 */
export function findAdSlots(MCP, { parentElement, addAsyncEvent }, initialLoad = false) {
  // Wait for any unloaded ad slots from previous call to this function
  const findAdSlotsPromise = Promise.resolve(previousRequest).then(() => {
    consolv.log('MCP findAdSlots: Looking for ad slots');
    const { findSlotsOnLoad, slotDefinitions } =
      MCP.Configurator.getConfiguration({ defaults: defaultConfig });

    let parent = document;
    if (parentElement && typeof parentElement === 'object' && parentElement.querySelectorAll) {
      parent = parentElement;
    }

    const newAdSlots = [];
    const existingSlots =
      Array.from(MCP.slots).map(([el, slot]) => ({ element: el, type: slot.type }));

    findSlotsOnLoad.forEach((entry) => {
      const searchEntry =
        (typeof entry === 'object') ?
          entry :
          { selector: `#${entry}, [data-slot-type="${entry}"]`, type: entry };

      const elements = Array.from(parent.querySelectorAll(searchEntry.selector));

      // Filters out existing ad slots that have been matched by the selector
      elements.filter(element => !existingSlots.find(slot => slot.element === element))
        .forEach((element, index) => {
          const similarSlots = existingSlots
            .filter(slot => slot.type === searchEntry.type)
            .map(item => item.element);

          // Restricts pixel distance between rendered elements of the same type
          let slotType = searchEntry.type;

          if (!slotDefinitions[slotType]) {
            consolv.log('MCP findAdSlots: Slot not defined', slotType);
            slotType = 'nop';
          }

          const {
            distanceFromSimilarSlot,
            distanceFromTop,
            nthInstance,
          } = slotDefinitions[slotType];

          if (distanceFromSimilarSlot && similarSlots.length) {
            const closestSimilarSlot = getDistanceFromElements({ element, siblings: similarSlots });

            if (closestSimilarSlot < distanceFromSimilarSlot) {
              slotType = 'nop';
            }
          }

          // Restricts ad slots within a certain distance of the page top
          if (distanceFromTop && element.offsetTop < distanceFromTop) {
            slotType = 'nop';
          }

          // Restricts the frequency of a particular type of ad slot on the page
          if (nthInstance) {
            const checkNth = nthCheck(nthInstance);
            if (!checkNth(index)) {
              slotType = 'nop';
            }
          }

          existingSlots.push({ element, type: slotType });

          const slot = MCP._defineAdSlot(slotType, element);
          newAdSlots.push(slot);
          MCP.dispatchEvent('slotCreated', { slot });
        });
    });

    previousRequest = Promise.all(newAdSlots).then((createdSlots) => {
      // const beforeEventParameters = MCP.dispatchEvent('foundAdSlots', { slots: createdSlots });
      // if (beforeEventParameters.defaultPrevented) return;
      // await beforeEventParameters.asyncEvents;

      const loadedSlots = createdSlots
        .filter(slot => slot && slot.enabled !== false && slot.meta.preventFindRefresh !== true);

      consolv.log('MCP: Found ad slots', loadedSlots);
      MCP.dispatchEvent('foundSlots', { slots: loadedSlots });

      if (loadedSlots.length) {
        loadedSlots.forEach((slot) => { slot.setMetaData('loaded', true); });
        MCP.refreshSlots(loadedSlots);
      }

      if (initialLoad) MCP.dispatchEvent('initialSlotsLoaded');

      return loadedSlots;
    });
    return previousRequest;
  });

  addAsyncEvent(findAdSlotsPromise);
}

export default function setupFindAdSlots(MCP) {
  MCP.addEventListener('ready', (e) => { findAdSlots(MCP, e, true); });
  MCP.addEventListener('contentUpdated', (e) => { findAdSlots(MCP, e); });
}
